import { defineStore } from 'pinia'
import { api } from '@/lib/api'

export const useTicketsStore = defineStore('tickets', {
  state: () => ({
    records: [],
    meta: { total_count: 0, page: 1, per_page: 25, total_pages: 0 },
    loading: false,
    loaded: false, // cold load vs. refresh (ver board.js)
    current: null,
    loadingOne: false,
    lastParams: {}, // últimos filtros — pro resync do realtime repetir a busca
  }),
  actions: {
    async fetchList(params = {}) {
      this.loading = true
      this.lastParams = params
      try {
        const clean = Object.fromEntries(
          Object.entries(params).filter(([, v]) => v != null && v !== '')
        )
        const qs = new URLSearchParams(clean).toString()
        const res = await api(`/api/tickets${qs ? `?${qs}` : ''}`)
        if (!res.ok) throw new Error('Falha ao carregar chamados')
        const data = await res.json()
        this.records = data.data
        this.meta = data.meta
        this.loaded = true
      } finally {
        this.loading = false
      }
    },

    async fetchOne(id) {
      // Mesmo chamado reaberto → mantém o cache na tela e revalida em silêncio.
      // Chamado diferente → limpa pra não mostrar o anterior enquanto carrega.
      const sameTicket = String(this.current?.id) === String(id)
      this.loadingOne = !sameTicket
      if (!sameTicket) this.current = null
      try {
        const res = await api(`/api/tickets/${id}`)
        if (!res.ok) throw new Error('Chamado não encontrado')
        this.current = await res.json()
      } finally {
        this.loadingOne = false
      }
    },

    async update(id, changes) {
      const res = await api(`/api/tickets/${id}`, {
        method: 'PATCH',
        body: { ticket: changes },
      })
      if (!res.ok) throw new Error('Falha ao atualizar o chamado')
      const updated = await res.json()
      const index = this.records.findIndex(ticket => ticket.id === updated.id)
      if (index >= 0) this.records[index] = { ...this.records[index], ...updated }
      if (this.current?.id === updated.id) this.current = updated
      return updated
    },

    async addComment(id, comment) {
      const res = await api(`/api/tickets/${id}/comments`, {
        method: 'POST',
        body: { comment },
      })
      if (!res.ok) throw new Error('Falha ao comentar')
      // Recarrega pra trazer o comentário + evento na timeline.
      await this.fetchOne(id)
    },

    // Realtime (ActionCable): upsert/remove na lista conforme a relevância.
    // Retorna 'added' | 'updated' | 'removed' | 'none'.
    applyRealtime(ticket, auth) {
      const relevant = auth.isAdmin || ticket.assignee_id === auth.user?.id
      const idx = this.records.findIndex(r => r.id === ticket.id)
      let action = 'none'

      if (relevant) {
        if (idx >= 0) {
          this.records[idx] = { ...this.records[idx], ...ticket }
          action = 'updated'
        } else {
          this.records = [ticket, ...this.records]
          this.meta.total_count += 1
          action = 'added'
        }
      } else if (idx >= 0) {
        this.records.splice(idx, 1)
        this.meta.total_count = Math.max(0, this.meta.total_count - 1)
        action = 'removed'
      }

      // Detalhe aberto desse chamado → recarrega pra refletir a mudança.
      if (this.current?.id === ticket.id) this.fetchOne(ticket.id)
      return action
    },

    refreshIfCurrent(id) {
      if (this.current?.id === id) this.fetchOne(id)
    },

    // Resync pós-reconexão do WebSocket: repete a última busca (mesmos filtros)
    // e revalida o chamado aberto — eventos perdidos no vão não são re-entregues.
    async resync() {
      if (this.loaded) await this.fetchList(this.lastParams)
      if (this.current) await this.fetchOne(this.current.id)
    },
  },
})
